const THUMBNAIL_MAX_WIDTH = 400;
const THUMBNAIL_JPEG_QUALITY = 0.75;

/**
 * Converts a Blob to a data URL without using large spread operations
 * that can overflow the call stack on big images.
 */
export async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Failed to convert blob to data URL'));
        return;
      }
      resolve(result);
    };
    reader.onerror = () => reject(new Error('Failed to read image blob'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Compresses a full-resolution PNG data URL into a small JPEG thumbnail.
 * Returns null on any failure — never returns the original large data URL,
 * as that would cause 413 errors downstream.
 */
export async function compressThumbnail(
  pngDataUrl: string,
  maxWidth: number = THUMBNAIL_MAX_WIDTH,
  quality: number = THUMBNAIL_JPEG_QUALITY
): Promise<string | null> {
  try {
    const startTime = performance.now();
    const originalSize = pngDataUrl.length;

    const img = new window.Image();
    img.src = pngDataUrl;

    await new Promise<void>((resolve, reject) => {
      if (img.complete && img.naturalWidth > 0) {
        resolve();
        return;
      }
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Failed to load thumbnail image for compression'));
    });

    const scale = maxWidth / img.naturalWidth;
    const targetWidth = Math.round(img.naturalWidth * scale);
    const targetHeight = Math.round(img.naturalHeight * scale);

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not get 2d canvas context');
    }

    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
    const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);

    canvas.width = 0;
    canvas.height = 0;

    return compressedDataUrl;
  } catch {
    return null;
  }
}

/**
 * Extracts base64 data and mimeType from a data URL.
 * Returns null if the string is not a valid data URL.
 */
export function parseDataUrl(dataUrl: string): { base64: string; mimeType: string } | null {
  const match = dataUrl.match(/^data:(image\/[\w+.-]+);base64,(.+)$/s);
  if (!match) return null;
  return { mimeType: match[1], base64: match[2] };
}

/**
 * Alias for parseDataUrl to keep semantic clarity in edit/stylize flows.
 */
export function extractBase64FromDataUrl(dataUrl: string): { base64: string; mimeType: string } | null {
  return parseDataUrl(dataUrl);
}

/**
 * Downscales and recompresses an image data URL to reduce payload size.
 * Uses JPEG output for predictable compression.
 */
export async function downscaleImageDataUrl(
  dataUrl: string,
  maxDimension: number,
  quality: number
): Promise<string> {
  const img = new window.Image();
  img.src = dataUrl;

  await new Promise<void>((resolve, reject) => {
    if (img.complete && img.naturalWidth > 0) {
      resolve();
      return;
    }
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('Failed to load image for downscaling'));
  });

  const largestSide = Math.max(img.naturalWidth, img.naturalHeight);
  const scale = largestSide > maxDimension ? maxDimension / largestSide : 1;
  const targetWidth = Math.max(1, Math.round(img.naturalWidth * scale));
  const targetHeight = Math.max(1, Math.round(img.naturalHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get 2d canvas context');
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

  const nextDataUrl = canvas.toDataURL('image/jpeg', quality);
  canvas.width = 0;
  canvas.height = 0;
  return nextDataUrl;
}

/**
 * Checks if a string is a data URI exceeding the given byte threshold.
 * Used as a safety guard to prevent oversized payloads from reaching the API.
 */
export function isOversizedDataUri(value: string | undefined | null, maxBytes: number = 200 * 1024): boolean {
  if (!value) return false;
  return value.startsWith('data:') && value.length > maxBytes;
}

/**
 * Replaces external image URLs in the DOM with inline data URIs so that
 * html-to-image can serialize them without relying on CORS cache or
 * SVG foreignObject cross-origin restrictions (problematic on mobile WebKit).
 *
 * Two-layer strategy per image:
 *   1. fetch() with CORS mode -> blob -> data URI (avoids canvas taint entirely)
 *   2. Canvas drawImage fallback (for when fetch fails but canvas is clean)
 *
 * This async approach fixes blank-image exports on devices where the browser
 * cache serves a non-CORS response, tainting the canvas silently.
 *
 * Returns a Map of the original src values so they can be restored afterward.
 */
export async function inlineExternalImages(
  element: HTMLElement
): Promise<Map<HTMLImageElement, string>> {
  const originals = new Map<HTMLImageElement, string>();
  const images = element.querySelectorAll('img');

  await Promise.all(
    Array.from(images).map(async (img) => {
      const src = img.src;
      if (!src || src.startsWith('data:') || src.startsWith('blob:')) return;

      // Strategy 1: fetch with CORS — most reliable, bypasses canvas taint
      try {
        const response = await fetch(src, { mode: 'cors' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        const dataUri = await blobToDataUrl(blob);
        originals.set(img, src);
        img.src = dataUri;
        return;
      } catch {
        // fetch failed (CORS rejected, network error, etc.) — try canvas
      }

      // Strategy 2: canvas drawImage fallback
      if (img.complete && img.naturalWidth > 0) {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0);
            const dataUri = canvas.toDataURL('image/png');
            canvas.width = 0;
            canvas.height = 0;
            originals.set(img, src);
            img.src = dataUri;
          }
        } catch {
          // Canvas tainted — keep original URL as last resort
        }
      }
    })
  );

  return originals;
}

/**
 * Pre-fetches a set of external image URLs into data URIs in a single
 * parallel batch. Used before batch export loops so that individual
 * slide captures can inline images from this cache instantly, avoiding
 * the per-slide CORS fetches that iOS WebKit throttles/blocks when
 * done sequentially across many slides.
 */
export async function prefetchAllImageUrls(
  urls: Set<string>
): Promise<Map<string, string>> {
  const cache = new Map<string, string>();
  if (urls.size === 0) return cache;

  await Promise.all(
    [...urls].map(async (url) => {
      try {
        const response = await fetch(url, { mode: 'cors' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        cache.set(url, await blobToDataUrl(blob));
      } catch {
        // URL stays uncached — inlineExternalImages will attempt individually as fallback
      }
    })
  );

  return cache;
}

/**
 * Synchronous (no-network) variant of inlineExternalImages that replaces
 * external image URLs in the DOM using a pre-fetched cache Map.
 * Returns original src values for later restoration via restoreOriginalImages.
 */
export function inlineImagesFromCache(
  element: HTMLElement,
  cache: Map<string, string>
): Map<HTMLImageElement, string> {
  const originals = new Map<HTMLImageElement, string>();
  element.querySelectorAll('img').forEach((img) => {
    const dataUri = cache.get(img.src);
    if (dataUri) {
      originals.set(img, img.src);
      img.src = dataUri;
    }
  });
  return originals;
}

/**
 * Restores original external URLs on images that were previously inlined.
 */
export function restoreOriginalImages(
  originals: Map<HTMLImageElement, string>
): void {
  originals.forEach((src, img) => { img.src = src; });
}
